import { ResizePanel } from '@affine/component/resize-panel';
import { AffineErrorComponent } from '@affine/core/components/affine/affine-error-boundary/affine-error-fallback';
import { workbenchRoutes } from '@affine/core/desktop/workbench-router';
import {
  appSettingAtom,
  FrameworkScope,
  useLiveData,
  useService,
} from '@toeverything/infra';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type RouteObject } from 'react-router-dom';

import { AppSidebarService } from '../../app-sidebar';
import type { View } from '../entities/view';
import { WorkbenchService } from '../services/workbench';
import { useBindWorkbenchToBrowserRouter } from './browser-adapter';
import { useBindWorkbenchToDesktopRouter } from './desktop-adapter';
import { RouteContainer } from './route-container';
import { SidebarContainer } from './sidebar/sidebar-container';
import { SplitView } from './split-view/split-view';
import { ViewIslandRegistryProvider } from './view-islands';
import { ViewRoot } from './view-root';
import * as styles from './workbench-root.css';
import { useHoverSupport } from '@affine/core/components/hooks/use-hover-support';

const useAdapter = BUILD_CONFIG.isElectron
  ? useBindWorkbenchToDesktopRouter
  : useBindWorkbenchToBrowserRouter;

const routes: RouteObject[] = [
  {
    element: <RouteContainer />,
    errorElement: <AffineErrorComponent />,
    children: workbenchRoutes,
  },
];

export const WorkbenchRoot = memo(() => {
  const workbench = useService(WorkbenchService).workbench;
  const appSidebarService = useService(AppSidebarService).sidebar;

  // for debugging
  (window as any).workbench = workbench;

  const views = useLiveData(workbench.views$);
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const activeView = useLiveData(workbench.activeView$);
  // 订阅活动视图的location变化
  const activeLocation = useLiveData(activeView?.location$);
  // const open = useLiveData(appSidebarService.open$);
  const smallScreenMode = useLiveData(appSidebarService.smallScreenMode$);
  const basename = location.pathname.match(/\/workspace\/[^/]+/g)?.[0] ?? '/';
  const isAllPage = useMemo(() => {
    return activeLocation.pathname.split('/').includes('all');
  }, [activeLocation.pathname]);
  const isHomePage = useMemo(() => {
    return activeLocation.pathname.split('/').includes('home');
  }, [activeLocation.pathname]);
  useAdapter(workbench, basename);

  const panelRenderer = useCallback(
    (view: View) => {
      return <WorkbenchView view={view} isHomePage={isHomePage} />;
    },
    [isHomePage]
  );

  const onMove = useCallback(
    (from: number, to: number) => {
      workbench.moveView(from, to);
    },
    [workbench]
  );

  useEffect(() => {
    workbench.updateBasename(basename);
  }, [basename, workbench]);

  return (
    <ViewIslandRegistryProvider>
      <SplitView
        className={`${styles.workbenchRootContainer} ${smallScreenMode || isAllPage ? styles.needPaddingRight : ''}`}
        views={views}
        renderer={panelRenderer}
        onMove={onMove}
        panelClassName={isHomePage ? styles.isHomePage : ''}
      />
      <WorkbenchSidebar />
    </ViewIslandRegistryProvider>
  );
});

WorkbenchRoot.displayName = 'memo(WorkbenchRoot)';

const WorkbenchView = ({
  view,
  isHomePage,
}: {
  view: View;
  isHomePage: boolean;
}) => {
  const workbench = useService(WorkbenchService).workbench;

  const handleOnFocus = useCallback(() => {
    workbench.active(view);
  }, [workbench, view]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      const element = containerRef.current;
      element.addEventListener('pointerdown', handleOnFocus, {
        capture: true,
      });
      return () => {
        element.removeEventListener('pointerdown', handleOnFocus, {
          capture: true,
        });
      };
    }
    return;
  }, [handleOnFocus]);

  return (
    <div className={`${styles.workbenchViewContainer}`} ref={containerRef}>
      <ViewRoot routes={routes} key={view.id} view={view} />
    </div>
  );
};

const MIN_SIDEBAR_WIDTH = 375;
const MAX_SIDEBAR_WIDTH =
  typeof window !== 'undefined' ? window.innerWidth - 625 : 1400;

const WorkbenchSidebar = () => {
  const { clientBorder } = useAtomValue(appSettingAtom);

  const [resizing, setResizing] = useState(false);

  const workbench = useService(WorkbenchService).workbench;
  const sidebarWidth = useLiveData(workbench.sidebarWidth$);
  const [width, setWidth] = useState(workbench.sidebarWidth$.value ?? 680);
  const supportsHover = useHoverSupport();

  const views = useLiveData(workbench.views$);

  const activeView = useLiveData(workbench.activeView$);
  const sidebarTabs = useLiveData(activeView?.sidebarTabs$);

  // 订阅活动视图的location变化
  const activeLocation = useLiveData(activeView?.location$);
  const sidebarOpen = useLiveData(workbench.sidebarOpen$);
  const [floating, setFloating] = useState(false);

  const onWidthChanged = useCallback(
    (width: number) => {
      workbench.setSidebarWidth(width);
      setWidth(width);
    },
    [workbench]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        workbench.openSidebar();
      } else {
        workbench.closeSidebar();
      }
    },
    [workbench]
  );

  useEffect(() => {
    const onResize = () => setFloating(!!(window.innerWidth < 768));
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (resizing) return;
    setWidth(sidebarWidth ?? 680);
  }, [resizing, sidebarWidth]);

  useEffect(() => {
    if (!supportsHover) {
      workbench.setSidebarOpen(false);
    }
  }, [supportsHover, workbench]);

  return (
    <ResizePanel
      floating={floating}
      resizeHandlePos="left"
      resizeHandleOffset={clientBorder && sidebarOpen ? 3 : 0}
      width={width}
      resizing={resizing}
      onResizing={setResizing}
      className={`${styles.workbenchSidebar} `}
      data-client-border={clientBorder && sidebarOpen}
      data-testid="workbench-sidebar"
      open={sidebarOpen}
      onOpen={handleOpenChange}
      onWidthChange={setWidth}
      onWidthChanged={onWidthChanged}
      minWidth={sidebarTabs?.length > 0 ? MIN_SIDEBAR_WIDTH : 0}
      maxWidth={sidebarTabs?.length > 0 ? MAX_SIDEBAR_WIDTH : 0}
      unmountOnExit={false}
      // isHomePage={isHomePage}
    >
      {views.map(view => (
        <FrameworkScope key={view.id} scope={view.scope}>
          <SidebarContainer
            style={{ display: activeView !== view ? 'none' : undefined }}
          />
        </FrameworkScope>
      ))}
    </ResizePanel>
  );
};
